import styled, { css } from "styled-components";
import { TEXT_COLORS, BACKGROUND_COLORS, COLORS, BORDER_COLORS } from "../../../constants/design/colors";
import { SPACING } from "../../../constants/design/spacing";
import { FONT_CSS } from "../../../constants/design/fontSize";

export const Title = styled.div`
  color: ${TEXT_COLORS.two};
  ${FONT_CSS.data.four};
  margin-top: ${SPACING.two}px;
`;

export const Value = styled.div<{ value: number }>`
  color: ${TEXT_COLORS.one};
  transition: 0.1s all ease-in-out;
  ${FONT_CSS.data.three};
`;

export const KeyValueWrapper = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  transition: 0.1s all ease-in-out;
  padding: ${SPACING.two}px;
  background-color: transparent;
  justify-content: space-between;

  > * {
    margin: auto 0;
  }

  &:hover {
    transition: 0.1s all ease-in-out;
    background-color: ${BACKGROUND_COLORS.four};

    *:not(${Title}) {
      color: ${COLORS.blue.one} !important;
    }
  }
`;

export const KeyValueContainer = styled.div<{ isMobile?: boolean }>`
  ${({ isMobile }) =>
    isMobile
      ? css`
          > * {
            border-radius: 6px;
            border: 1px solid ${BORDER_COLORS.three};
            background-color: ${BACKGROUND_COLORS.four};
          }
        `
      : css`
          margin: ${SPACING.two}px;

          > * {
            border-radius: 6px;
            border: 1px solid ${BORDER_COLORS.three};
            background-color: ${BACKGROUND_COLORS.four};
          }
        `}
  display: grid;
  grid-gap: ${SPACING.two}px;
  grid-template-columns: repeat(1, 1fr);

  &:not(:last-child) {
    margin-top: ${SPACING.two}px;
  }
`;

export const GreekWrapper = styled.div`
  border: 1px solid ${BORDER_COLORS.one};
  border-radius: 6px;
  padding: ${SPACING.three}px;
  grid-column: span 1;
  display: grid;
  grid-template-columns: calc(32px + ${SPACING.three * 2}px) 1fr;
`;

export const GreekLogo = styled.div`
  display: grid;
  align-items: center;
  text-align: center;
  padding: ${SPACING.two}px;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: ${BACKGROUND_COLORS.component};
  margin: auto 0;
  margin-right: ${SPACING.three}px;
  border: 1px solid ${BORDER_COLORS.two};
`;

export const GreekStatsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: 1fr;
  grid-gap: ${SPACING.four}px;
  overflow-x: scroll;
  margin: ${SPACING.three}px 0;
`;

export const GreekStat = styled.div<{ value?: number }>`
  display: flex;
  justify-content: space-between;
  padding: 2px 0;

  span:first-of-type {
    ${FONT_CSS.header.six};
    color: ${TEXT_COLORS.two};
  }

  span:last-of-type {
    ${FONT_CSS.header.six};

    color: ${({ value }) => (value === 0 ? TEXT_COLORS.three : TEXT_COLORS.one)};
  }

  &:last-of-type {
    border-top: 1px solid ${BORDER_COLORS.one};
    margin-top: ${SPACING.one}px;
    padding-top: ${SPACING.one}px;

    span:last-of-type {
      color: ${COLORS.blue.one};
    }
  }

  &:hover {
    > * {
      transition: 0.2s all ease-in-out;
      color: ${COLORS.blue.one} !important;
    }
  }
`;

export const StatTitle = styled.div<{ isMobile?: boolean }>`
  ${FONT_CSS.label.three};
  ${({ isMobile }) =>
    isMobile
      ? css`
          margin: ${SPACING.two}px;
        `
      : css`
          margin: ${SPACING.three}px;
        `}
  margin-bottom: ${SPACING.two}px;
  color: ${TEXT_COLORS.two};
  text-transform: uppercase;

  &:not(:first-of-type) {
    margin-top: ${SPACING.four}px;
  }
`;

const assetBreakdownGrid = css`
  display: grid;
  grid-template-columns: minmax(92px, 1fr) repeat(3, minmax(58px, 0.72fr)) auto;
  gap: ${SPACING.two}px;
  align-items: center;
`;

export const AssetBreakdownWrapper = styled.div<{ isMobile?: boolean }>`
  margin: ${({ isMobile }) => (isMobile ? 0 : `${SPACING.two}px`)};
  overflow-x: auto;
  border: 1px solid ${BORDER_COLORS.two};
  border-radius: 8px;
  background: ${BACKGROUND_COLORS.four};
`;

export const AssetBreakdownTable = styled.div`
  min-width: 390px;
`;

export const AssetBreakdownHeader = styled.div`
  ${assetBreakdownGrid};
  ${FONT_CSS.label.three};
  padding: ${SPACING.two}px ${SPACING.three}px;
  color: ${TEXT_COLORS.three};
  text-transform: uppercase;

  > span:not(:first-child) {
    text-align: right;
  }
`;

export const AssetBreakdownRow = styled.div`
  ${assetBreakdownGrid};
  min-height: 48px;
  padding: ${SPACING.two}px ${SPACING.three}px;
  border-top: 1px solid ${BORDER_COLORS.two};

  &:hover {
    background: ${BACKGROUND_COLORS.two};
  }
`;

export const AssetIdentity = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${SPACING.two}px;
`;

export const AssetLogo = styled.img`
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  border-radius: 50%;
  object-fit: contain;
`;

export const AssetLogoFallback = styled.span`
  ${FONT_CSS.label.two};
  display: grid;
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  place-items: center;
  border: 1px solid ${BORDER_COLORS.one};
  border-radius: 50%;
  color: ${TEXT_COLORS.two};
  background: ${BACKGROUND_COLORS.two};
`;

export const AssetName = styled.span`
  ${FONT_CSS.label.two};
  overflow: hidden;
  color: ${TEXT_COLORS.one};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const AssetBalanceValue = styled.span<{
  tone?: "available" | "value";
}>`
  ${FONT_CSS.data.three};
  min-width: 0;
  overflow: hidden;
  color: ${({ tone }) => {
    if (tone === "available") return COLORS.orange.one;
    if (tone === "value") return COLORS.blue.one;
    return TEXT_COLORS.one;
  }};
  font-variant-numeric: tabular-nums;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ConfiscateButton = styled.button`
  ${FONT_CSS.label.three};
  height: 28px;
  padding: 0 ${SPACING.two}px;
  border: 1px solid ${COLORS.red.three};
  border-radius: 6px;
  color: ${COLORS.red.one};
  background: ${COLORS.red.six};
  cursor: pointer;
  transition: background-color 140ms ease-out, transform 140ms ease-out;

  &:hover {
    background: ${COLORS.red.five};
  }

  &:active {
    transform: scale(0.97);
  }
`;

export const AssetBreakdownEmpty = styled.div`
  ${FONT_CSS.body.four};
  padding: ${SPACING.three}px;
  color: ${TEXT_COLORS.three};
  text-align: center;
`;
